import { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

import { REMOVE_SNACKBAR } from "../../../actions/ActionTypes";

import type { ReactNode } from "react";
import type { ExternalToast } from "sonner";

import type { AppState } from "../../../reducers";

type ToastKey = string | number;
type ToastVariant = "error" | "info" | "success" | "warning";

interface NotificationOptions {
  details?: ReactNode;
  variant?: ToastVariant;
}

interface Notification {
  dismissed?: boolean;
  key: ToastKey;
  message: string;
  options?: NotificationOptions;
}

interface FlashMessageState {
  notifications: Notification[];
}

const displayed = new Set<ToastKey>();

function Notifier(): null {
  const dispatch = useDispatch();
  const notifications = useSelector(
    (state: AppState) =>
      (state.flashMessageReducer as FlashMessageState).notifications,
  );

  useEffect(() => {
    const removeNotification = (key: ToastKey) => {
      dispatch({
        key,
        type: REMOVE_SNACKBAR,
      });
      displayed.delete(key);
    };

    notifications.forEach(
      ({ dismissed = false, key, message, options = {} }) => {
        if (dismissed) {
          toast.dismiss(key);
          return;
        }

        if (displayed.has(key)) return;

        const toastOptions: ExternalToast = {
          description: options.details,
          id: key,
          onAutoClose: () => removeNotification(key),
          onDismiss: () => removeNotification(key),
        };

        switch (options.variant) {
          case "success":
            toast.success(message, toastOptions);
            break;
          case "error":
            toast.error(message, toastOptions);
            break;
          case "info":
            toast.info(message, toastOptions);
            break;
          case "warning":
            toast.warning(message, toastOptions);
            break;
          default:
            toast(message, toastOptions);
        }

        displayed.add(key);
      },
    );
  }, [dispatch, notifications]);

  return null;
}

export default Notifier;
